import React from "react";
import { cssVariables } from "./css_variables";
import { stylex } from "./stylex";
import { IconName } from "./icon";
import { PickerDisplay } from "./picker_display";

interface Option<TValue extends string | number = any> {
  label: string;
  value: TValue;
  disabled?: boolean;
}

interface PickerProps<TValue extends string | number = any> {
  testID?: string;
  value?: TValue;
  onChange: (value: TValue) => void;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  title?: string;
  display?: React.ReactNode;
  iconBefore?: IconName;
}

export function Picker<TValue extends string | number = any>(
  props: PickerProps<TValue>
) {
  const {
    placeholder,
    options,
    value,
    onChange,
    disabled,
    display,
    title,
    iconBefore,
    testID,
    invalid,
  } = props;

  const selected = options.find((opt) => opt.value === value);

  return (
    <div style={styles("root")}>
      {display || (
        <PickerDisplay
          title={title || selected?.label || placeholder}
          disabled={disabled}
          iconBefore={iconBefore}
          invalid={invalid}
        />
      )}
      <select
        disabled={disabled}
        style={styles("select", disabled && "disabled")}
        data-testid={testID}
        value={value || ""}
        onChange={(event) => {
          if (typeof value === "number") {
            onChange(Number(event.currentTarget.value) as TValue);
          } else {
            onChange(event.currentTarget.value as TValue);
          }
        }}
      >
        {placeholder && (
          <option disabled value="">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            disabled={option.disabled}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const styles = stylex.create({
  root: {
    boxSizing: "border-box",
    borderRadius: cssVariables.radius,
    position: "relative",
  },
  select: {
    opacity: 0,
    position: "absolute",
    left: 0,
    top: 0,
    backgroundColor: "rgba(0, 0, 0, 0)",
    height: "100%",
    boxSizing: "border-box",
    width: "100%",
    border: "none",
    WebkitAppearance: "none",
    fontSize: cssVariables.textSizeMD,
    cursor: "pointer",
  },
  disabled: {
    cursor: "not-allowed",
  },
});
